import * as React from 'react';
import {TruncatedLink} from './TruncatedLink';
import {LinkBase} from './LinkBase';
import type {LinkProps} from './_types';

/**
 * # Link
 *
 * A componentized Link, an inline element used to navigate to content or, if necessary, trigger some event.
 *
 * ## Usage
 *
 * ```jsx
 * import {Link} from '@packages/systems/spring';
 *
 * const Example = () => {
 *   return (
 *     <Link href='https://webflow.com/legal/terms/'>Terms of Service</Link>
 *   );
 * }
 * ```
 *
 * @component
 */

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      children,
      truncateToLines,
      truncationLimit,
      truncationPosition,
      truncationTooltipProps,
      ...props
    },
    ref
  ) => {
    const shouldTruncate = Boolean(
      truncateToLines || (truncationLimit && typeof children === 'string')
    );

    if (shouldTruncate) {
      return (
        <TruncatedLink
          truncateToLines={truncateToLines}
          truncationLimit={truncationLimit}
          truncationPosition={truncationPosition}
          truncationTooltipProps={truncationTooltipProps}
          {...props}
          ref={ref}
        >
          {children}
        </TruncatedLink>
      );
    } else {
      return (
        <LinkBase {...props} ref={ref}>
          {children}
        </LinkBase>
      );
    }
  }
);

Link.displayName = 'Link';
Link.toString = () => '[data-sc*=Link]';

export default Link;
